import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";

const UPLOAD_BASE = process.env.REACT_APP_UPLOAD_URL || "";

const resolveAssetUrl = (url?: string) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${UPLOAD_BASE}${url}`;
  return `${UPLOAD_BASE}/${url}`;
};

type DashboardItem = {
  courseId: string;
  title: string;
  slug: string;
  thumbnail: string;
  progress: { completed: number; total: number; percent: number };
  accessExpiresAt: string | null;
  isExpired: boolean;
  passedAssessment: boolean;
  hasCertificate: boolean;
  certificateId: string | null;
};

export function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState<DashboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const enrolled = searchParams.get("enrolled") === "1";

  useEffect(() => {
    api
      .get("/dashboard")
      .then((res) => {
        if (res.data.success) setItems(res.data.data || []);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (!user) return null;

  const completedCount = items.filter(
    (i) => i.progress.total > 0 && i.progress.completed === i.progress.total,
  ).length;
  const inProgressCount = items.filter((i) => i.progress.percent > 0 && i.progress.percent < 100).length;

  return (
    <div className="min-h-full bg-stone/40">
      {/* Hero strip */}
      <div className="bg-ink-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
          <p className="text-gold-300/90 font-semibold text-xs tracking-[0.2em] uppercase">
            Dashboard
          </p>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight mt-2">
            My courses
          </h1>
          <p className="mt-2 text-slate-400 text-sm max-w-lg">
            {items.length === 0
              ? "Enroll in a course to start learning and earn certificates."
              : "Track your progress and continue where you left off."}
          </p>
          {enrolled && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-green-500/20 px-4 py-2 text-sm font-medium text-green-300">
              <span className="h-2 w-2 rounded-full bg-green-400" />
              You’re enrolled. Start learning below.
            </div>
          )}
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold-400/30 to-transparent" aria-hidden />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
        {items.length > 0 && !loading && (
          <div className="flex flex-wrap gap-4 mb-10">
            <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-soft min-w-[140px]">
              <p className="text-2xl font-semibold text-ink-950 tabular-nums">{items.length}</p>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-0.5">Enrolled</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-soft min-w-[140px]">
              <p className="text-2xl font-semibold text-gold-500 tabular-nums">{inProgressCount}</p>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-0.5">In progress</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-soft min-w-[140px]">
              <p className="text-2xl font-semibold text-green-600 tabular-nums">{completedCount}</p>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-0.5">Completed</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse rounded-2xl border border-gray-200 bg-white overflow-hidden">
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-48 h-36 sm:h-auto bg-gray-100" />
                  <div className="p-6 flex-1 space-y-4">
                    <div className="h-5 w-2/3 bg-gray-200 rounded" />
                    <div className="h-2 w-full bg-gray-100 rounded-full" />
                    <div className="flex gap-3">
                      <div className="h-10 w-24 bg-gray-200 rounded-lg" />
                      <div className="h-10 w-28 bg-gray-100 rounded-lg" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 md:p-16 text-center shadow-soft">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gold-400/10 border border-gold-400/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-gold-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            </div>
            <h2 className="font-serif text-xl font-semibold text-ink-950 mt-6">
              No courses yet
            </h2>
            <p className="mt-2 text-gray-600 max-w-sm mx-auto">
              Browse the catalog and subscribe to a plan (or use an access code) to get access to all courses.
            </p>
            <Link
              to="/courses"
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-gold-400 px-6 py-3.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors shadow-md"
            >
              Browse courses
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {items.map((item) => (
              <div
                key={item.courseId}
                className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 hover:border-gold-400/30"
              >
                <div className="flex flex-col sm:flex-row">
                  <div className="sm:w-52 h-40 sm:h-auto sm:min-h-[180px] bg-stone flex items-center justify-center overflow-hidden shrink-0">
                    {item.thumbnail ? (
                      <img
                        src={resolveAssetUrl(item.thumbnail)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-gray-400">
                        <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" />
                        </svg>
                        <span className="text-xs mt-1 block">Course</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                    <div className="min-w-0 flex-1">
                      <h2 className="font-serif text-xl font-semibold text-ink-950 tracking-tight">
                        {item.title}
                      </h2>
                      <div className="mt-4 flex items-center gap-4 flex-wrap">
                        <div className="flex-1 min-w-[120px] max-w-[200px] h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-gold-400 to-gold-500 rounded-full transition-all duration-500"
                            style={{ width: `${item.progress.percent}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 font-medium">
                          {item.progress.completed} of {item.progress.total} lessons
                        </span>
                      </div>
                      {item.accessExpiresAt && (
                        <p className="mt-2 text-xs text-gray-500">
                          Access expires {new Date(item.accessExpiresAt).toLocaleDateString()}
                          {item.isExpired && (
                            <span className="text-red-600 font-medium ml-1">(expired)</span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 shrink-0">
                      <Link
                        to={`/dashboard/courses/${item.slug}`}
                        className="inline-flex items-center gap-2 rounded-lg bg-gold-400 px-5 py-2.5 text-sm font-semibold text-ink-950 hover:bg-gold-300 transition-colors shadow-sm"
                      >
                        {item.progress.completed === 0 ? "Start course" : "Resume"}
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                      </Link>
                      {item.progress.total > 0 && item.progress.completed === item.progress.total && (
                        <Link
                          to={`/dashboard/courses/${item.slug}/certificate`}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-ink-950 hover:bg-stone/50 hover:border-gold-400/40 transition-colors"
                        >
                          <svg className="w-4 h-4 text-gold-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Certificate
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="mt-10 pt-8 border-t border-gray-200 flex flex-wrap gap-4">
            <Link
              to="/courses"
              className="text-sm font-semibold text-gold-500 hover:text-gold-400 transition-colors"
            >
              Browse more courses →
            </Link>
            <Link
              to="/dashboard/certificates"
              className="text-sm font-semibold text-gray-600 hover:text-ink-950 transition-colors"
            >
              View certificates →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
